/* «Говорящий кружок» на движке шаблона: состояние экрана S (features/app/engine.ts) ↔ входы формы сервера (план кружка §3 K8).
   Вид (CircleView) — то, что экран говорит заказу словами модели: кто говорит, чем, каким голосом, формат, вид, движение, звук, кадр и фрагмент.
   - viewFromEngine(h) — вид из S и крючков движка (зеркало S → входы в syncFoot, план кружка §2);
   - inputsOf(form, view, prev) — входы по виду; пишет только опубликованные ключи (circle-model.circleKeys: старый набор шаблона и контракт
     экрана), правила — родные (use-circle-screen.ts: группа источника, кто говорит, речь, голоса, «Зациклить», движение, звук, кадр, фрагмент);
     id загрузок пишет мост загрузок (media-bridge) — здесь они только снимаются, если файл уже не того, кто говорит (лицо одно, речь одна);
   - viewOf(form, inputs) — обратно: только то, что входы определяют (повтор из «Истории», «Изменить» после перезагрузки, заявка родной формы);
   - applyView(h, view) — вид в S (кто говорит, речь, голос, формат, вид, движение, звук, «Зациклить», стиль анимации); файлы — у сервера экрана.
   Чистые inputsOf и viewOf проходят круг: viewOf(form, inputsOf(form, view, {})) ⊆ view (circle-inputs.test.ts). */
import type { StudioForm } from '../studio/studio-api';
import {
  circleAudioRevoice,
  circleCharacterId,
  circleCropValue,
  circleCropWrite,
  circleFace,
  circleFormat,
  circleGroup,
  circleKeys,
  circleLoopOn,
  circleMotionOn,
  circleMotionValue,
  circleMotionWrite,
  circleOccasions,
  circleOwnAllowed,
  circlePolicy,
  circleSfxOn,
  circleSpeech,
  circleTrimValue,
  circleTrimWrite,
  circleWho,
  type CircleCrop,
  type CircleSpeech,
  type CircleWho,
} from '../studio/circle/circle-model';
import { toolDefault, toolField } from '../studio/circle/tool-form';
import { selfRef, type SelfRef } from '../studio/engine-kit/people-bridge';

type Inputs = Record<string, string>;
/** Звук своей записи или своего видео: как в записи, голосом персонажа, другим голосом */
export type VoiceChoice = 'src' | 'char' | 'voice';
export interface CircleView {
  who: CircleWho | null;
  /** Персонаж (who char) */
  char: number | null;
  /** Близкий (who person) */
  person: number | null;
  /** «Вы» (who me): свои фото или вариант */
  self: SelfRef | null;
  /** Поле речи: текст или запись (S.speech) */
  speech: 'text' | 'audio';
  /** Своё видео своей речью («Оставить мою речь») */
  keep: boolean;
  text: string;
  /** Голос озвучки текста */
  voice: number | null;
  /** Голос персонажа, который говорит (по умолчанию у текста; «Голос персонажа» у записи) */
  charVoice: number | null;
  /** Звук записи или своего видео */
  vm: VoiceChoice;
  /** Голос переозвучки записи или видео (vm char или voice) */
  audioVoice: number | null;
  /** circle или пропорции видео (9:16, 16:9, 1:1, 4:5) */
  format: string;
  /** Вид персонажа ('' — обычный) */
  skin: string;
  motion: { do: string; not: string };
  sfx: string;
  loop: boolean;
  /** Стиль анимации звука («Кружок из аудио») */
  viz: string;
  /** Кадр своего снимка или ролика (центр и приближение) */
  crop: CircleCrop | null;
  /** Фрагмент своего видео, мс */
  trim: { start: number; end: number } | null;
  /** id готовых загрузок файлов, которые держит сервер экрана (снимок, ролик, запись): тот, кто говорит, снова свой файл — id снова во входах */
  uploads?: Partial<Record<'photo' | 'video' | 'audio', string>>;
}

/** Что звучит в заказе: текст, запись или своя речь видео (speechKind шаблона) */
export function speechOf(view: Pick<CircleView, 'who' | 'speech' | 'keep'>): CircleSpeech {
  if (view.who === 'video' && view.keep) return 'own';
  return view.speech === 'audio' ? 'audio' : 'text';
}
const isDefaultCrop = (c: CircleCrop) => Math.abs(c.cx - 0.5) < 1e-4 && Math.abs(c.cy - 0.5) < 1e-4 && Math.abs(c.z - 1) < 1e-3;
const num = (value: unknown): number | null => {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
};
const published = (form: StudioForm, key: string | undefined, value: string) => {
  if (!key) return false;
  const values = toolField(form, key)?.values ?? [];
  return !values.length || values.includes(value);
};

/** Повод, чей текст ровно в поле (чип отмечен), и только опубликованный */
export function occasionOf(form: StudioForm, text: string): string | null {
  const trimmed = text.trim();
  return circleOccasions(form).find(([, preset]) => preset === trimmed)?.[0] ?? null;
}

/** Входы формы по виду (родные правила, только опубликованные ключи). prev — нынешние входы: id загрузок того, кто говорит, и
    неизвестные виду ключи остаются */
export function inputsOf(form: StudioForm | null | undefined, view: CircleView, prev: Inputs): Inputs {
  if (!form) return prev;
  const keys = circleKeys(form);
  const policy = circlePolicy(form);
  const next: Inputs = { ...prev };
  const put = (key: string | undefined, value: string | null | undefined) => {
    if (!key) return;
    if (value === null || value === undefined || value === '') delete next[key];
    else next[key] = value;
  };
  const who = view.who;
  const speech = speechOf(view);
  /* кто говорит: группа источника и сам выбор */
  put(keys.source, who ? circleGroup(who) : null);
  put(keys.who, who);
  for (const key of [keys.cast, keys.avatar, keys.me, keys.person]) put(key, null);
  if (who === 'char' && view.char) {
    if (keys.avatar) put(keys.avatar, String(view.char));
    else put(keys.cast, `character:${view.char}`);
  } else if (who === 'person' && view.person) {
    if (keys.person) put(keys.person, String(view.person));
    else put(keys.cast, `person:${view.person}`);
  } else if (who === 'me' && view.self) {
    const avatar = view.self.src === 'avatar' ? view.self.avatar_id : null;
    if (keys.me) put(keys.me, JSON.stringify(avatar ? { src: 'avatar', avatar_id: avatar } : { src: 'photos' }));
    else put(keys.cast, avatar ? `user_avatar:${avatar}` : 'self');
  }
  /* загрузки: лицо одно и речь одна — файл не того, кто говорит, в заказ не идёт (сам файл остаётся у моста и в ряду) */
  const ids = view.uploads ?? {};
  put(keys.photo, who === 'photo' ? (ids.photo ?? (keys.photo ? prev[keys.photo] : null)) : null);
  put(keys.video, who === 'video' ? (ids.video ?? (keys.video ? prev[keys.video] : null)) : null);
  put(keys.audio, speech === 'audio' ? (ids.audio ?? (keys.audio ? prev[keys.audio] : null)) : null);
  /* речь */
  if (speech === 'text') {
    put(keys.text, view.text.trim() ? view.text : null);
    put(keys.occasion, occasionOf(form, view.text));
  } else {
    put(keys.text, null);
    put(keys.occasion, null);
  }
  put(keys.speech, who && who !== 'audio' ? speech : null);
  /* голоса: озвучка текста — голос текста; запись и своя речь — как в записи или голос переозвучки (только если форма её разрешает) */
  const tv = keys.textVoice,
    av = keys.audioVoice;
  if (speech === 'text') {
    if (av && av !== tv) put(av, null);
    put(tv, view.voice ? String(view.voice) : null);
    put(keys.voiceMode, view.voice ? (view.voice === view.charVoice && who === 'char' ? 'char' : 'voice') : null);
  } else {
    if (tv && tv !== av) put(tv, null);
    const revoice = speech === 'own' ? policy?.video_revoice === true : policy ? circleAudioRevoice(policy) : true;
    const voice = revoice && view.vm !== 'src' ? view.audioVoice : null;
    put(av, voice ? String(voice) : null);
    put(keys.voiceMode, voice ? view.vm : 'src');
  }
  /* формат: только опубликованные значения */
  if (keys.format && published(form, keys.format, view.format)) put(keys.format, view.format);
  /* вид, движение, звук, «Зациклить», стиль анимации — по тому, кто говорит, и флагам политики */
  put(keys.skin, who === 'char' && view.skin ? view.skin : null);
  const motionOn = circleMotionOn(form, who);
  let out = circleMotionWrite(form, next, motionOn ? view.motion : { do: '', not: '' });
  const sfxOn = circleSfxOn(form, who, speech);
  if (keys.sfx) {
    if (sfxOn && view.sfx.trim()) out[keys.sfx] = view.sfx;
    else delete out[keys.sfx];
  }
  if (keys.loop) {
    if (circleLoopOn(form, who, speech) && view.loop) out[keys.loop] = 'true';
    else delete out[keys.loop];
  }
  if (keys.viz) {
    if (who === 'audio' && view.viz && published(form, keys.viz, view.viz)) out[keys.viz] = view.viz;
    else delete out[keys.viz];
  }
  /* кадр своего снимка или ролика (по центру — не пишется), фрагмент своего видео */
  out = circleCropWrite(form, out, (who === 'photo' || who === 'video') && view.crop && !isDefaultCrop(view.crop) ? view.crop : null);
  out = circleTrimWrite(form, out, who === 'video' && view.trim ? view.trim : null);
  return out;
}

/** «Вы» из входов: me {src, avatar_id} контракта или строка состава self / user_avatar:<id> */
function selfOf(form: StudioForm, inputs: Inputs): SelfRef | null {
  const keys = circleKeys(form);
  if (keys.me && inputs[keys.me]) {
    try {
      const value = JSON.parse(inputs[keys.me]) as { src?: unknown; avatar_id?: unknown };
      const id = num(value.avatar_id);
      if (value.src === 'avatar' && id) return { src: 'avatar', avatar_id: id };
      if (value.src === 'photos') return { src: 'photos' };
    } catch {
      return null;
    }
  }
  const cast = keys.cast ? (inputs[keys.cast] ?? '') : '';
  if (cast === 'self') return { src: 'photos' };
  const id = cast.startsWith('user_avatar:') ? num(cast.slice(12)) : null;
  return id ? { src: 'avatar', avatar_id: id } : null;
}

/** Вид по входам: только то, что входы определяют (нет кого говорит — нет и who; нет поля — нет значения) */
export function viewOf(form: StudioForm | null | undefined, inputs: Inputs): Partial<CircleView> {
  if (!form) return {};
  const keys = circleKeys(form);
  const out: Partial<CircleView> = {};
  const who = circleWho(form, inputs, null);
  const has = (key: string | undefined) => !!key && inputs[key] !== undefined && inputs[key] !== '';
  if (who) {
    out.who = who;
    if (who === 'char') out.char = circleCharacterId(form, inputs);
    if (who === 'person') {
      const face = circleFace(form, inputs);
      out.person = face.startsWith('person:') ? num(face.slice(7)) : null;
    }
    if (who === 'me') out.self = selfOf(form, inputs);
  }
  const determined = who !== null || has(keys.speech) || has(keys.text) || has(keys.audio);
  if (determined) {
    const speech = circleSpeech(form, inputs, who);
    out.keep = speech === 'own' && circleOwnAllowed(form);
    out.speech = speech === 'audio' ? 'audio' : 'text';
    if (speech === 'text') {
      out.text = keys.text ? (inputs[keys.text] ?? '') : '';
      out.voice = keys.textVoice ? num(inputs[keys.textVoice]) : null;
    } else {
      const id = keys.audioVoice ? num(inputs[keys.audioVoice]) : null;
      const mode = keys.voiceMode ? inputs[keys.voiceMode] : '';
      out.audioVoice = id;
      out.vm = !id ? 'src' : mode === 'char' ? 'char' : 'voice';
    }
  }
  if (has(keys.format)) out.format = circleFormat(form, inputs);
  if (keys.skin) out.skin = inputs[keys.skin] ?? '';
  if (keys.motion || keys.movement || keys.negative) out.motion = circleMotionValue(form, inputs);
  if (keys.sfx) out.sfx = inputs[keys.sfx] ?? '';
  if (keys.loop) out.loop = inputs[keys.loop] === 'true';
  if (keys.viz && has(keys.viz)) out.viz = inputs[keys.viz];
  const crop = circleCropValue(form, inputs);
  if (crop) out.crop = crop;
  const trim = circleTrimValue(form, inputs);
  if (trim) out.trim = trim;
  return out;
}

/** Кто говорит по умолчанию формы: значение по умолчанию поля состава или персонажа (у шаблона-кружка — его герой, ch шаблона),
    когда входы о том, кто говорит, молчат (подстановка его не пишет) */
export function defaultFace(form: StudioForm | null | undefined): Pick<CircleView, 'who' | 'char'> | null {
  if (!form) return null;
  const keys = circleKeys(form);
  const defaults: Inputs = {};
  for (const key of [keys.cast, keys.avatar]) {
    const value = key ? toolDefault(toolField(form, key)) : undefined;
    if (key && value) defaults[key] = value;
  }
  const v = Object.keys(defaults).length ? viewOf(form, defaults) : {};
  return v.who === 'char' && v.char ? { who: 'char', char: v.char } : null;
}

/* ---------- движок ---------- */

interface EngineFile {
  crop?: CircleCrop;
  trim?: { from: number; to: number };
  dur?: number;
}
/** Крючки движка, которые читает и пишет вид (часть make({...}) из engine.ts) */
export interface CircleViewHooks {
  S: {
    pick: string | null;
    cid: unknown;
    ppl: Array<{ id: number | string; act: unknown; own: string }>;
    files: { photo: EngineFile | null; video: EngineFile | null };
    speech: string;
    keep: boolean;
    text: string;
    voice: unknown;
    vm: Record<string, string>;
    fmt: string;
    ar: string;
    arUser: boolean;
    skin: string;
    mv: string;
    mvNo: string;
    sfx: string;
    loop: boolean;
    viz: string;
    audio: boolean;
    [key: string]: unknown;
  };
  readonly P: { setIn(on: boolean): void; inFrame(): boolean };
  who(): CircleWho | null;
  char(): { id: number | string; voices?: unknown[] };
  keepOn(): boolean;
  vctx(): 'aud' | 'own' | null;
  vm(c?: string | null): VoiceChoice | null;
  round(): boolean;
  limits(): { min: number; max: number; rec: number };
}
/** Вид из S. charVoice — голос персонажа по умолчанию (сервер экрана знает его по карточке персонажа) */
export function viewFromEngine(h: CircleViewHooks, charVoice: number | null, uploads: CircleView['uploads'] = {}): CircleView {
  const { S } = h;
  const who = h.who();
  const c = h.vctx();
  const vm = c ? (h.vm(c) ?? 'src') : 'src';
  const own = who === 'photo' || who === 'video' ? S.files[who] : null;
  const video = who === 'video' ? S.files.video : null;
  const rec = h.limits().rec;
  let trim: CircleView['trim'] = null;
  if (video && (video.dur ?? 0) > 0) {
    const t = video.trim && video.trim.from >= 0 && video.trim.to <= (video.dur ?? 0) ? video.trim : { from: 0, to: Math.min(video.dur ?? 0, rec) };
    trim = { start: Math.round(t.from * 1000), end: Math.round(t.to * 1000) };
  }
  const voice = num(S.voice);
  return {
    who,
    char: who === 'char' ? num(h.char().id) : null,
    person: who === 'person' ? num(S.ppl[0]?.id) : null,
    self: who === 'me' ? selfRef() : null,
    speech: S.speech === 'audio' ? 'audio' : 'text',
    keep: h.keepOn(),
    text: S.text,
    voice: c ? null : voice,
    charVoice: who === 'char' ? charVoice : null,
    vm,
    audioVoice: c ? (vm === 'char' ? charVoice : vm === 'voice' ? voice : null) : null,
    format: h.round() ? 'circle' : S.ar,
    skin: S.skin && S.skin !== 'def' ? S.skin : '',
    motion: { do: S.mv, not: S.mvNo },
    sfx: S.sfx,
    loop: !!S.loop,
    viz: S.viz,
    crop: own?.crop ?? null,
    trim,
    uploads,
  };
}

/** Вид в S: кто говорит, речь, голос, формат и остальное — только определённые поля (кадр и фрагмент — у файлов, их ставит сервер экрана).
    keep — что вход экрана уже решил сам (кто говорит своё фото, видео или аудио; формат входа; речь — новые слова или своя речь видео,
    текст или запись): из вида не берётся */
export function applyView(h: CircleViewHooks, view: Partial<CircleView>, keep: { who?: boolean; format?: boolean; speech?: boolean } = {}): void {
  const { S } = h;
  if (view.who && !keep.who) {
    S.audio = view.who === 'audio';
    if (view.who === 'char') {
      S.pick = 'char';
      S.ppl = [];
      h.P.setIn(false);
      if (view.char && String(S.cid) !== String(view.char)) {
        S.cid = view.char;
        S.skin = 'def';
      }
    } else if (view.who === 'person') {
      S.pick = null;
      h.P.setIn(false);
      S.ppl = view.person ? [{ id: view.person, act: null, own: '' }] : [];
    } else if (view.who === 'me') {
      S.pick = null;
      S.ppl = [];
      h.P.setIn(true);
    } else if (view.who === 'photo' || view.who === 'video') {
      S.pick = view.who;
      S.ppl = [];
      h.P.setIn(false);
    }
  }
  if (view.speech && !keep.speech) S.speech = view.speech;
  if (view.keep !== undefined && !keep.speech) S.keep = view.keep;
  if (view.text !== undefined) S.text = view.text;
  if (view.voice !== undefined) S.voice = view.voice;
  const c = h.vctx();
  if (view.vm && c) {
    S.vm[c] = view.vm;
    if (view.vm === 'voice' && view.audioVoice) S.voice = view.audioVoice;
  }
  if (view.format && !keep.format) {
    if (view.format === 'circle') S.fmt = 'circle';
    else {
      S.fmt = 'video';
      S.ar = view.format;
      S.arUser = true;
    }
  }
  if (view.skin !== undefined) S.skin = view.skin || 'def';
  if (view.motion) {
    S.mv = view.motion.do;
    S.mvNo = view.motion.not;
  }
  if (view.sfx !== undefined) S.sfx = view.sfx;
  if (view.loop !== undefined) S.loop = view.loop;
  if (view.viz) S.viz = view.viz;
}
